import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Wifi, Loader2, Sun, Moon } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useAdminTheme } from "@/context/AdminThemeContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const [email, setEmail] = useState("admin@netonlain.co.ke");
  const [password, setPassword] = useState("admin123");
  const [busy, setBusy] = useState(false);
  const { login, error } = useAuth();
  const { theme, toggleTheme } = useAdminTheme();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const ok = await login(email, password);
    setBusy(false);
    if (ok) navigate("/admin/dashboard");
  };

  return (
    <div className={`nhp-${theme} min-h-screen relative bg-nhp-bg text-nhp-text`}>
      <button
        type="button"
        data-testid="login-theme-toggle-btn"
        onClick={toggleTheme}
        className="absolute top-6 right-6 z-10 h-10 w-10 grid place-items-center border border-nhp-border bg-nhp-surface/80 backdrop-blur-xl text-nhp-muted hover:text-nhp-text"
        aria-label="Toggle light/dark theme"
      >
        {theme === "dark" ? <Sun className="h-4 w-4" strokeWidth={1.75} /> : <Moon className="h-4 w-4" strokeWidth={1.75} />}
      </button>
      {/* Nairobi skyline background */}
      <div
        className="absolute inset-0 opacity-[0.18]"
        style={{
          backgroundImage:
            "url(https://images.unsplash.com/photo-1780131856279-04cfe938410e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxfHxuYWlyb2JpJTIwa2VueWElMjBza3lsaW5lJTIwc3Vuc2V0fGVufDB8fHx8MTc4NTE2NjI3M3ww&ixlib=rb-4.1.0&q=85)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-nhp-bg/70 via-nhp-bg/85 to-nhp-bg" />
      <div className="absolute inset-0 nhp-grid-bg opacity-40" />

      <div className="relative min-h-screen grid lg:grid-cols-2">
        {/* Left branding */}
        <div className="hidden lg:flex flex-col justify-between p-12">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 grid place-items-center bg-nhp-primary text-nhp-on-primary">
              <Wifi className="h-6 w-6" strokeWidth={2.5} />
            </div>
            <div>
              <div className="font-heading font-black text-xl leading-none">NetOnLain</div>
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-nhp-muted mt-1">
                Hotspot Platform · v1.0
              </div>
            </div>
          </div>

          <div className="max-w-md">
            <div className="font-mono text-xs uppercase tracking-[0.25em] text-nhp-primary mb-4">
              // ADMIN CONSOLE
            </div>
            <h1 className="font-heading font-black text-5xl tracking-tighter leading-[0.95]">
              Command every hotspot from one calm dashboard.
            </h1>
            <p className="mt-6 text-nhp-muted text-base leading-relaxed">
              Centralized RADIUS, M-Pesa payments, MikroTik provisioning and
              subscription lifecycles — all wired into a single control plane
              tailored for Kenyan ISPs.
            </p>

            <div className="mt-10 grid grid-cols-3 gap-6">
              {[
                ["24/7", "AUTOMATION"],
                ["Multi", "SITE"],
                ["M-PESA", "NATIVE"],
              ].map(([k, v]) => (
                <div key={v} className="border-l border-nhp-border pl-3">
                  <div className="font-mono font-bold text-nhp-primary text-lg">{k}</div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted mt-1">{v}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-nhp-muted">
            © 2026 NetOnLain Ltd · Nairobi
          </div>
        </div>

        {/* Right login form */}
        <div className="flex items-center justify-center p-6 sm:p-12">
          <div className="w-full max-w-md">
            <div className="border border-nhp-border bg-nhp-surface/80 backdrop-blur-xl p-8">
              <div className="lg:hidden flex items-center gap-3 mb-8">
                <div className="h-10 w-10 grid place-items-center bg-nhp-primary text-nhp-on-primary">
                  <Wifi className="h-5 w-5" strokeWidth={2.5} />
                </div>
                <div>
                  <div className="font-heading font-black text-lg leading-none">NetOnLain</div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted mt-1">
                    Hotspot Platform
                  </div>
                </div>
              </div>

              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-nhp-primary mb-2">
                // AUTHENTICATION REQUIRED
              </div>
              <h2 className="font-heading font-black text-3xl tracking-tight">Sign in</h2>
              <p className="text-sm text-nhp-muted mt-2">
                Use your NetOnLain administrator credentials to continue.
              </p>

              <form onSubmit={submit} className="mt-8 space-y-5">
                <div>
                  <Label htmlFor="email" className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted">
                    Email
                  </Label>
                  <Input
                    id="email"
                    data-testid="login-email-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="mt-2 bg-nhp-bg border-nhp-border text-nhp-text h-11 rounded-none focus-visible:ring-nhp-primary focus-visible:ring-1 focus-visible:ring-offset-0"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="password" className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted">
                    Password
                  </Label>
                  <Input
                    id="password"
                    data-testid="login-password-input"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="mt-2 bg-nhp-bg border-nhp-border text-nhp-text h-11 rounded-none focus-visible:ring-nhp-primary focus-visible:ring-1 focus-visible:ring-offset-0"
                    required
                  />
                </div>

                {error && (
                  <div
                    data-testid="login-error"
                    className="border border-nhp-critical/40 bg-nhp-critical/10 px-3 py-2 text-sm text-nhp-critical font-mono"
                  >
                    {error}
                  </div>
                )}

                <Button
                  type="submit"
                  data-testid="login-submit-btn"
                  disabled={busy}
                  className="w-full h-11 rounded-none bg-nhp-primary hover:bg-nhp-primary-hover text-nhp-on-primary font-bold tracking-wide"
                >
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "SIGN IN"}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
